// Packages
import React, { useState, useEffect, useMemo } from "react";
import { connect, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useIntl, FormattedMessage } from 'react-intl';
// Components
import LoyaltyWidgetMobile from "../../mobileComponents/loyaltyWidgetMobile";
import OutOfStockWarning from "../../mobileComponents/outOfStockWarningMobile";
import CartItems from "../../mobileComponents/cartItems/cartItems";
// Redux Operations
import { cartOperations } from "../../state/features/cart";
import { pageOperations } from "../../state/features/page";
import { loaderOperations } from "../../state/features/loader";
import { errorOperations } from "../../state/features/error";
import { areasOperations } from "../../state/features/areas";
// Helpers, Utils etc.
import {
  formatDecimal,
  calculateAmountsAndBreakdowns,
} from "../../helpers/itemCalculations";
import { getSubdomainPath } from "../../helpers/pathUtils";
import {
  updateItemWithCartIdInCart,
  removeItemWithCartId,
} from "../../helpers/cartFunctions";
import { validateCartItems, outOfStockItemsList, syncCartOnLocationChange } from "../../helpers/cartFunctions";
import { usePromotionsContext } from "../../hooks/usePromotionsContext";
import { combineCarts } from "../../features/promotions/helpers/utils";
import PromoCodesContainerMobile from "../../mobileComponents/promoCodesContainer";
import OrderDetailsFooter from "../../mobileComponents/orderDetailsFooter";


interface IOrderDetailsScreenProps {
  cart: any;
  setCart: any;
  menu: any;
  orderType: any;
  hasLoyalty: boolean;
  redeemedPoints: any;
  discount: any;
  user: any;
  loyalty: any;
  deliveryAreas: any;
  areaId: any;
  outlet: any;
  storeOpen: boolean;
  location: any;
  selectedArea: any;
  showAreaSelect: boolean;
  fetchOrderDetailsConfigurations: any;
  showLoader: any;
  updateError: any;
  setArea: any;
  outOfStock: any[];
  recentOrder: any;
  availablePromotions: any[];
  appliedPromotions: any[];
  allowStoreClosedOrdering: boolean;
  hasValidPromotions: boolean;
  validateCart(cart: any[]): Promise<any>;
  hideLoader(): void;
}

function OrderDetailsScreen(props: IOrderDetailsScreenProps) {
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [paymentDetails, setPaymentDetails] = useState({} as any);
  const { appliedPromotionsData, cartVerificationPayload, freeItemCart } =
    usePromotionsContext();

  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();

  const cartDetails = useMemo(() => {
    const currentUserCart = combineCarts(
      appliedPromotionsData.promotionAppliedCart,
      freeItemCart
    );
    let amountBreakdowns: any = calculateAmountsAndBreakdowns(
      currentUserCart,
      props.menu,
      props.orderType,
      props.discount,
      props.redeemedPoints,
      props.selectedArea,
      props.outlet.delivery_charge_id,
      0, // tips is 0
      appliedPromotionsData.orderPromotion
    );
    let taxBreakdown = amountBreakdowns.taxBreakdown ?? [];
    let subTotal = amountBreakdowns?.subTotal ?? 0;
    let total = amountBreakdowns?.total ?? 0;
    let taxTotal = amountBreakdowns?.taxTotal ?? 0;
    let surchargeBreakDown = amountBreakdowns.surchargeBreakDown ?? [];
    let orderPromotionDiscount = amountBreakdowns.orderPromotionDiscount ?? 0;
    let appliedPromotionName = "";
    if (
      appliedPromotionsData.orderPromotion &&
      appliedPromotionsData.orderPromotion.length > 0
    ) {
      appliedPromotionName = appliedPromotionsData.orderPromotion[0].name;
    }

    return {
      subTotal,
      total,
      taxTotal,
      surchargeBreakDown,
      orderPromotionDiscount,
      appliedPromotionName,
      taxBreakdown,
    };
  }, [
    props.cart,
    JSON.stringify(appliedPromotionsData.promotionAppliedCart),
    JSON.stringify(appliedPromotionsData.orderPromotion),
    JSON.stringify(freeItemCart),
  ]);

  useEffect(() => {
    setCartItems(appliedPromotionsData.promotionAppliedCart);
    if (props.deliveryAreas && props.orderType === "delivery") {
      findDeliveryArea();
    }
  }, [
    JSON.stringify(appliedPromotionsData.promotionAppliedCart),
    props.areaId,
    cartDetails.subTotal,
  ]);

  useEffect(() => {
    props.fetchOrderDetailsConfigurations().then((response: any) => {
      if (!response.error) {
        const validItems = validateCartItems(
          response.payload.data.menu.categories,
          JSON.parse(localStorage.cartItems || "[]"),
          itemsNotInMenuError
        );
        updateItemsInCart(
          syncCartOnLocationChange(response.payload.data.menu.categories, validItems, itemsNotInMenuError)
        );
      }
    });
    verifyPaymentStatus();
  }, []);

  useEffect(() => {
    if (props.showAreaSelect) {
      props.updateError({
        titleCode: "error.oops",
        messageCode: "error.unable_to_process_order",
        redirectionUrl: "/m",
      });
    }
  }, [props.showAreaSelect]);

  useEffect(() => {
    window.scrollTo(0, 0);
    updateItemsInCart(props.cart.items);
  }, [
    JSON.stringify(props.cart.items),
    JSON.stringify(appliedPromotionsData.promotionAppliedCart),
    JSON.stringify(appliedPromotionsData.orderPromotion),
  ]);

  const findDeliveryArea = () => {
    let area = props.deliveryAreas.find((area: any) => {
      return area.id == props.areaId;
    });
    props.setArea(area);
  };

  const outOfStockInCart = useMemo(() => {
    return outOfStockItemsList(cartItems, props.outOfStock);
  }, [cartItems]);

  const amountToReachMinOrderValue = useMemo(() => {
    const totalAmount = cartDetails.subTotal;
    if (props.deliveryAreas && props.orderType === "delivery") {
      if (Number(totalAmount) < Number(props.selectedArea.min_order_value)) {
        return Number(props.selectedArea.min_order_value) - Number(totalAmount);
      } else {
        return 0;
      }
    }
    return 0;
  }, [
    cartDetails.subTotal,
    props.selectedArea.min_order_value,
    props.deliveryAreas,
    props.orderType,
  ]);

  const updateItemsInCart = (cartItems: any) => {
    const promotionAppliedCart =
      appliedPromotionsData.promotionAppliedCart.length > 0
        ? appliedPromotionsData.promotionAppliedCart
        : JSON.parse(localStorage.getItem('promotion_applied_cart') || '[]');
    const currentCartItems =
      cartItems.length > 0
        ? cartItems
        : JSON.parse(localStorage.cartItems || '[]');
    const currentUserCart = combineCarts(promotionAppliedCart, freeItemCart);
    let cart = calculateAmountsAndBreakdowns(
      currentUserCart,
      props.menu,
      props.orderType,
      props.discount,
      props.redeemedPoints,
      props.selectedArea,
      props.outlet.delivery_charge_id,
      0,
      appliedPromotionsData.orderPromotion
    ) as any;
    cart.items = currentCartItems;

    props.setCart(cart);
  };

  const itemsNotInMenuError = () => {
    props.updateError({
      title: intl.formatMessage({ id: "error.oops", defaultMessage: "Oops!" }),
      message: intl.formatMessage({ id: "error.items_in_cart_not_in_menu", defaultMessage: "Items in cart are not part of current available menu." }),
    });
  }

  const verifyPaymentStatus = () => {
    let urlParams = new URLSearchParams(props.location.search);
    let paymentStatus = urlParams.get("payment_status");
    let message = urlParams.get("message");

    if (paymentStatus == "failed") {
      setPaymentDetails({
        status: paymentStatus,
        message: message,
      });
    }
  }

  const userCartItems = useMemo(() => {
    return combineCarts(
      appliedPromotionsData.promotionAppliedCart,
      freeItemCart,
    );
  }, [
    JSON.stringify(appliedPromotionsData.promotionAppliedCart),
    JSON.stringify(freeItemCart),
  ]);

  const incrementCount = (item: any) => {
    if(props.menu.out_of_stock_items.includes(item.id)){
      return;
    }
    item.count = item.count + 1;
    updateItemWithCartIdInCart(item, props.cart.items, props.setCart);
  };

  const decrementCount = (item: any) => {
    item.count = item.count - 1;
    updateItemWithCartIdInCart(item, props.cart.items, props.setCart);
  };

  const removeItem = (item: any) => {
    removeItemWithCartId(item, props.cart.items, props.setCart);
  };

  const handleCartValidationFailure = () => {
    dispatch(
      errorOperations.updateError({
        title: intl.formatMessage({ id: "error.oops", defaultMessage: "Oops!" }),
        message: intl.formatMessage({
          id: "error.something_went_wrong",
          defaultMessage: "Something went wrong. Please try again later.",
        }),
      })
    );
  };

  const onCheckoutClick = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    props.showLoader();
    if (!props.user?.id) {
      localStorage.setItem("postRegistrationPage", "/m/checkout");
      const loginPath = getSubdomainPath("/m/login");
      history.push(loginPath);
      props.hideLoader();
    } else {
      try {
        props.showLoader();
        const result = await props.validateCart(cartVerificationPayload);
        if (!result.error && result.payload.data.validated) {
          const checkoutPath = getSubdomainPath("/m/checkout");
          history.push(checkoutPath);
        } else {
          props.hideLoader();
          handleCartValidationFailure();
        }
      } catch (error) {
        handleCartValidationFailure();
      } finally {
        props.hideLoader();
      }
    }
  };

  const disableCheckoutButton = useMemo(() => {
    return (
      cartItems?.length === 0 ||
      amountToReachMinOrderValue ||
      (!props.storeOpen && !props.allowStoreClosedOrdering) ||
      outOfStockInCart?.length > 0 ||
      props.recentOrder
    );
  }, [
    cartItems,
    amountToReachMinOrderValue,
    props.storeOpen,
    outOfStockInCart,
    props.recentOrder,
  ]);

  return (
    <div className="order-details-screen mt-0">
      {paymentDetails.status == "failed" ? (
        <div className="payment-failure">
          <p className="error-header">
            <FormattedMessage
              id="payment.payment_failed"
              defaultMessage="Payment Failed"
            />
          </p>
          <p className="error-message">
            <FormattedMessage
              id="payment.unable_to_process_payment"
              defaultMessage="Sorry, we were unable to process your payment. Your card was not charged. Please try again or use a different payment method."
            />
          </p>
          <p className="error">{paymentDetails.message}</p>
        </div>
      ) : null}

      {outOfStockInCart.length ? (
        <div className="out-of-stock-warning">
          <OutOfStockWarning outOfStockItems={outOfStockInCart} />
        </div>
      ) : null}

      <div className="order-details">
        <CartItems cartItems={userCartItems} />
      </div>
      <div className="other-details footer">
        <PromoCodesContainerMobile />
        {props.hasLoyalty ? <LoyaltyWidgetMobile /> : null}
        <OrderDetailsFooter
          cartDetails={cartDetails}
          amountToReachMinOrderValue={amountToReachMinOrderValue}
          disableCheckoutButton={disableCheckoutButton}
          onCheckoutClick={onCheckoutClick}
        />
      </div>
    </div>
  );
}

const mapStateToProps = (state: any) => {
  let menu = state.menu;
  let cart = state.cart;
  let orderType = state.session.order_type;
  let loyaltyConfig = state.loyaltyConfig || {};
  let hasLoyalty = loyaltyConfig[orderType]?.enabled;
  let redeemedPoints = state.loyalty?.redeemedPoints;
  let discount = state.discount;
  let user = state.user;
  let deliveryAreas = state.outlet?.delivery_areas;
  let areaId = state.session.area_id;
  let outlet = state.outlet;
  let loyalty = state.loyalty;
  let storeOpen = state.storeTimings.open;
  let selectedArea = state.areas.selectedArea;
  let showAreaSelect = state.session.showAreaSelect;
  let outOfStock = state.menu.out_of_stock_items;
  let recentOrder = state.recentOrder;
  let availablePromotions = state.promotions.availablePromotions;
  let appliedPromotions = state.promotions.appliedPromotions;
  let allowStoreClosedOrdering = state.company?.configuration?.allow_order_when_closed || false;
  let hasValidPromotions = state.company.has_promotions || false;

  return {
    cart,
    menu,
    orderType,
    hasLoyalty,
    redeemedPoints,
    discount,
    user,
    deliveryAreas,
    areaId,
    outlet,
    loyalty,
    storeOpen,
    selectedArea,
    showAreaSelect,
    outOfStock,
    recentOrder,
    availablePromotions,
    appliedPromotions,
    allowStoreClosedOrdering,
    hasValidPromotions,
  };
};

const mapDispatchToProps = {
  setCart: cartOperations.setCart,
  fetchOrderDetailsConfigurations:
    pageOperations.fetchOrderDetailsConfigurations,
  showLoader: loaderOperations.showLoader,
  updateError: errorOperations.updateError,
  setArea: areasOperations.setArea,
  validateCart: cartOperations.validateCart,
  hideLoader: loaderOperations.hideLoader,
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderDetailsScreen);
